<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

import useResponsive from '@/composables/useResponsive';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';

const metricsStore = useMetricsStore();
const { themes } = storeToRefs(metricsStore);

const productsStore = useProductsStore();

const globalConfigurationStore = useGlobalConfigurationStore();
const { currentLocale } = storeToRefs(globalConfigurationStore);

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const { isMobile } = useResponsive();

const selectedThemeIds = ref<string[]>([]);
const showAll = ref(false);
const maxVisibleThemes = isMobile.value ? 4 : 7;

onMounted(() => {
  if (
    selectedThemeIds.value.length > 0
      && themes.value.findIndex(theme => selectedThemeIds.value.includes(theme.id)) >= maxVisibleThemes
  ) {
    showAll.value = true;
  }
});

const displayedThemes = computed(() => {
  return showAll.value ? themes.value : themes.value.slice(0, maxVisibleThemes);
});

const existingThemes = filters.value.selections.find(it => it.filterProperties.type === 'taxonomyThemes');
if (existingThemes?.selection?.length > 0) {
  selectedThemeIds.value = [...existingThemes.selection];
}

function getTranslatedName(theme: any) {
  return theme.translatedName.find((it: any) => it.lang === currentLocale.value)?.value ?? theme.name;
}

function toggleTheme(theme: typeof themes.value[number]) {
  const index = selectedThemeIds.value.indexOf(theme.id);
  if (index > -1) {
    selectedThemeIds.value.splice(index, 1);
  }
  else {
    selectedThemeIds.value.push(theme.id);
  }

  const themeFilter = filters.value.selections.find(it => it.filterProperties.type === 'taxonomyThemes');
  if (themeFilter) {
    themeFilter.selection = [...selectedThemeIds.value];
  }
  else {
    filters.value.selections.push({
      filterProperties: { type: 'taxonomyThemes' },
      selection: [...selectedThemeIds.value],
    });
  }

  const existingTypesIndex = filters.value.selections.findIndex(it => it.filterProperties.type === 'taxonomyTypes');
  if (existingTypesIndex > -1) {
    filters.value.selections.splice(existingTypesIndex, 1);
  }

  const crossedTaxonomies = filters.value.metrics.find(it => it.type === 'crossedTaxonomyCount');
  if (crossedTaxonomies) {
    crossedTaxonomies.crossedTaxonomies.themes = [...selectedThemeIds.value];
  }
  else {
    filters.value.metrics.push({
      type: 'crossedTaxonomyCount',
      taxonomyTypes: ['TYPES'],
      crossedTaxonomies: {
        themes: [...selectedThemeIds.value],
      },
    });
  }

  filters.value.sorts = filters.value.sorts.filter((it: any) => it.type !== 'search');

  filtersStore.saveToStorage(filters.value);
  productsStore.fetchProductsList(filters.value);
}
</script>

<template>
  <ul id="options-list" role="listbox" class="options-list">
    <div
      v-for="(option, index) in displayedThemes"
      :key="index"
      class="option-row"
      tabindex="0"
    >
      <li
        role="option"
        tabindex="0"
        class="option"
        :aria-selected="selectedThemeIds.includes(option.id)"
        :class="{ selected: selectedThemeIds.includes(option.id) }"
        @click="toggleTheme(option)"
        @keydown.enter="toggleTheme(option)"
      >
        <div class="label-container">
          <label
            :id="index.toString()"
            :for="index.toString()"
            :class="{ disabled: option.count === 0 }"
          >
            {{ getTranslatedName(option) }}
          </label>
          <span style="margin-left: 4px;">({{ option.count }})</span>
        </div>
        <input
          :id="index.toString()"
          type="checkbox"
          :name="option.name"
          :value="option"
          :checked="selectedThemeIds.includes(option.id)"
          :aria-describedby="index.toString()"
        >
      </li>
    </div>
    <div
      v-if="themes.length > maxVisibleThemes"
      role="button"
      class="toggle-view-button"
      @click="showAll = !showAll"
    >
      {{ showAll ? 'Voir moins' : 'Tout voir' }}
      <svg
        v-if="!showAll"
        width="12"
        height="7"
        viewBox="0 0 12 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="chevron-icon"
      >
        <path
          d="M11 1.3333L6.33338 6L1.66671 1.3333"
          stroke="var(--yb-color-primary)"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <svg
        v-else
        width="12"
        height="7"
        viewBox="0 0 12 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="chevron-icon"
      >
        <path
          d="M11 6L6.33338 1.3333L1.66671 6"
          stroke="var(--yb-color-primary)"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </ul>
</template>

<style lang="css" scoped>
ul {
  margin-block-start: 0;
  margin-block-end: 0;
  padding-inline-start: 0;
}

.options-list {
  list-style-type: none;
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-sm);
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: var(--yb-radius-sm);
  outline: none;
  padding-block: var(--yb-spacing-xs);
  padding-right: var(--yb-spacing-xs);
}

.option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-wrap: nowrap;
  color: var(--yb-color-black);
  cursor: pointer;

  & > * {
    pointer-events: none;
  }

  .label-container {
    display: flex;
    width: 90%;
    gap: var(--yb-spacing-xs);
  }

  label {
    max-width: 80%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  input[type='checkbox'] {
    appearance: none;
    -webkit-appearance: none;
    position: relative;
    width: 16px;
    aspect-ratio: 1;
    border: 1px solid var(--yb-color-mediumgray);
    border-radius: 2px;
    background-color: var(--yb-color-white);
    margin-inline: var(--yb-spacing-xs);
    accent-color: var(--yb-color-primary);
  }

  input[type='checkbox']:checked {
    background-color: var(--yb-color-primary);
    border-color: var(--yb-color-primary);
    color: var(--yb-color-white);
  }

  input[type='checkbox']:checked::before {
    content: '\2713';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    text-align: center;
    color: var(--yb-color-white);
    font-size: 16px;
  }
}

.toggle-view-button {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-xs);
  background: none;
  border: none;
  color: var(--yb-color-primary);
  padding: 0;
  margin-top: var(--yb-spacing-sm);
  cursor: pointer;
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-semi-bold);
  border-bottom: solid 1px;
}

.option-row:hover,
.option-row:focus,
.option-row:focus-visible {
  background-color: var(--yb-color-lightgray);
}
</style>
